import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { motion } from 'framer-motion';
import { TrendingUp, Users, Star } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { safeArray } from '@/components/utils/safeArray';

export default function CommunityRecommendations({ userConditions = [] }) {
  const { data: reviews = [] } = useQuery({
    queryKey: ['product-reviews'],
    queryFn: async () => {
      const data = await base44.entities.ProductReview.filter({ is_public: true });
      return safeArray(data);
    },
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const data = await base44.entities.Product.list('-created_date');
      return safeArray(data);
    },
  });

  // Calculate top products based on reviews
  const getTopProducts = () => {
    const productRatings = {};
    
    safeArray(reviews).forEach(review => {
      if (!productRatings[review.product_id]) {
        productRatings[review.product_id] = {
          total: 0,
          count: 0,
          reviews: []
        };
      }
      productRatings[review.product_id].total += review.rating;
      productRatings[review.product_id].count++;
      productRatings[review.product_id].reviews.push(review);
    });

    const topProducts = Object.entries(productRatings)
      .map(([productId, data]) => ({
        productId,
        avgRating: data.total / data.count,
        reviewCount: data.count
      }))
      .sort((a, b) => {
        // Sort by rating first, then by review count
        if (Math.abs(a.avgRating - b.avgRating) < 0.5) {
          return b.reviewCount - a.reviewCount;
        }
        return b.avgRating - a.avgRating;
      })
      .slice(0, 5);

    return topProducts.map(item => {
      const product = safeArray(products).find(p => p.id === item.productId);
      return product ? { ...product, avgRating: item.avgRating, reviewCount: item.reviewCount } : null;
    }).filter(Boolean);
  };

  const topProducts = getTopProducts();

  if (topProducts.length === 0) {
    return null;
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-gray-900">Recomendaciones de la Comunidad</h3>
      </div>
      
      <p className="text-sm text-gray-600 mb-4">
        Los productos mejor valorados por usuarios con perfiles similares
      </p>

      <div className="space-y-3">
        {safeArray(topProducts).map((product, index) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"
          >
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-14 h-14 rounded-lg object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-gray-200" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{product.name}</p>
              {product.brand && <p className="text-xs text-gray-500">{product.brand}</p>}
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <span className="text-xs font-medium text-gray-700">{product.avgRating.toFixed(1)}</span>
                </div>
                <span className="text-xs text-gray-400">•</span>
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-500">{product.reviewCount} reseña{product.reviewCount !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
            {product.scaneat_score !== undefined && (
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm
                ${product.traffic_light === 'green' ? 'bg-emerald-50 text-emerald-700' :
                  product.traffic_light === 'yellow' ? 'bg-amber-50 text-amber-700' :
                  'bg-red-50 text-red-700'}
              `}>
                {product.scaneat_score}
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </Card>
  );
}